import { useMemo, useRef, useState } from 'react';
import { IconAlertTriangle, IconLoader2, IconMail } from '@tabler/icons-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import novaOpsApi from '@/lib/novaOpsApi';
import { findCurrentSmsPortalBlist, type SmsDelivery } from './smsPortalBlist';

type Props = {
  messages: SmsDelivery[];
  msisdn?: string | null;
  mvno?: string | null;
  canSubmit: boolean;
};

export function SmsPortalBlistPanel({
  messages,
  msisdn,
  mvno,
  canSubmit,
}: Props) {
  const blockedDelivery = useMemo(
    () => findCurrentSmsPortalBlist(messages),
    [messages],
  );
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [evidence, setEvidence] = useState<File | null>(null);
  const evidenceInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submittedRequest, setSubmittedRequest] = useState<{
    deliveryId: string;
    submittedAt: string;
  } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!blockedDelivery) return null;

  const canonicalMsisdn = String(msisdn ?? '').replace(/[^0-9]/g, '');
  const latestAttempt =
    blockedDelivery.failed_at ||
    blockedDelivery.submitted_at ||
    blockedDelivery.created_at ||
    '';

  const submit = async () => {
    if (!evidence || !consentConfirmed) return;
    setSubmitting(true);
    setError('');
    try {
      const form = new FormData();
      form.set('msisdn', canonicalMsisdn);
      form.set('mvno', mvno || 'Unknown');
      form.set('purpose', blockedDelivery.purpose);
      form.set(
        'providerRef',
        blockedDelivery.provider_ref ||
          blockedDelivery.provider_message_id ||
          '',
      );
      form.set('latestAttempt', latestAttempt);
      form.set('consentConfirmed', 'true');
      form.set('evidence', evidence);
      const response = await novaOpsApi.post<{
        submittedAt: string;
      }>('/ops/customers/messages/blist-escalation', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmittedRequest({
        deliveryId: blockedDelivery.id,
        submittedAt: response.data.submittedAt,
      });
      setDialogOpen(false);
    } catch (caught) {
      const message = caught as {
        response?: {
          data?: { message?: string; error?: { message?: string } };
        };
        message?: string;
      };
      setError(
        message.response?.data?.error?.message ||
          message.response?.data?.message ||
          message.message ||
          'Could not send the SMSPortal request.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Alert variant="destructive" className="mb-4">
        <IconAlertTriangle />
        <AlertTitle>SMS OTP delivery blocked</AlertTitle>
        <AlertDescription className="space-y-4">
          <p>
            SMSPortal returned BLIST for the latest {blockedDelivery.purpose}{' '}
            attempt. Customer Care can submit the standard whitelist request
            after obtaining written consent.
          </p>
          <dl className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="font-medium">MSISDN</dt>
              <dd>{canonicalMsisdn || 'Unavailable'}</dd>
            </div>
            <div>
              <dt className="font-medium">MVNO</dt>
              <dd>{mvno || 'Unavailable'}</dd>
            </div>
            <div>
              <dt className="font-medium">Journey</dt>
              <dd>{blockedDelivery.purpose}</dd>
            </div>
            <div>
              <dt className="font-medium">Provider ref</dt>
              <dd>
                {blockedDelivery.provider_ref ||
                  blockedDelivery.provider_message_id ||
                  'Unavailable'}
              </dd>
            </div>
          </dl>
          {submittedRequest?.deliveryId === blockedDelivery.id ? (
            <p className="text-sm">
              Whitelist request sent to help@smsportal.com on{' '}
              {new Date(submittedRequest.submittedAt).toLocaleString()} with the
              consent evidence attached.
            </p>
          ) : (
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                disabled={!canSubmit || !canonicalMsisdn}
                onClick={() => {
                  setError('');
                  setDialogOpen(true);
                }}
              >
                <IconMail className="size-4" />
                Send details to SMSPortal
              </Button>
              {!canSubmit && (
                <p className="text-sm">
                  Write access to Customer Care actions is required to submit
                  this request.
                </p>
              )}
            </div>
          )}
        </AlertDescription>
      </Alert>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Send whitelist request to SMSPortal</DialogTitle>
            <DialogDescription>
              Attach the customer’s written consent before sending the standard
              request for {canonicalMsisdn}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="smsportal-consent-evidence">
                Written consent evidence
              </Label>
              <input
                ref={evidenceInputRef}
                id="smsportal-consent-evidence"
                type="file"
                className="sr-only"
                tabIndex={-1}
                accept="image/png,image/jpeg,image/webp,application/pdf"
                aria-describedby="smsportal-consent-help smsportal-consent-selection"
                disabled={!canSubmit || submitting}
                onChange={(event) => {
                  const selectedFile = event.target.files?.[0];
                  if (selectedFile) setEvidence(selectedFile);
                  // Keep the selection on cancel and allow choosing the same file again.
                  event.target.value = '';
                }}
              />
              <div className="flex items-center gap-3">
                <span
                  id="smsportal-consent-selection"
                  role="status"
                  className="min-w-0 flex-1 break-all text-sm"
                >
                  {evidence?.name || 'No file selected'}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canSubmit || submitting}
                  aria-describedby="smsportal-consent-selection"
                  onClick={() => evidenceInputRef.current?.click()}
                >
                  {evidence ? 'Change file' : 'Choose file'}
                </Button>
              </div>
              <p
                id="smsportal-consent-help"
                className="text-xs text-muted-foreground"
              >
                Attach a screenshot or PDF showing the customer explicitly
                agreed to receive transactional and OTP messages. Maximum 5 MB.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="smsportal-consent-confirmed"
                checked={consentConfirmed}
                disabled={!canSubmit || submitting}
                onCheckedChange={(checked) =>
                  setConsentConfirmed(checked === true)
                }
              />
              <Label
                htmlFor="smsportal-consent-confirmed"
                className="font-normal"
              >
                I confirm this evidence records the customer’s explicit consent
                to receive transactional and OTP SMS messages.
              </Label>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button
              type="button"
              disabled={
                !canSubmit ||
                !evidence ||
                !consentConfirmed ||
                !canonicalMsisdn ||
                submitting
              }
              onClick={() => void submit()}
            >
              {submitting ? (
                <IconLoader2 className="size-4 animate-spin" />
              ) : (
                <IconMail className="size-4" />
              )}
              Send standard request to SMSPortal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
